import StatusListManager from '@/pages/Business/pages/LogisticOperator/components/StatusListManager';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import type { ShippingStatus } from '@/pages/Business/pages/LogisticOperator/models';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { FormCheckbox, FormTextInput, GlobalButton } from '@/components';
import { WebhookHeadersField } from '../../../components';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import {
  integrationWebhookFormSchema,
  integrationWebhookFormToCreatePayload,
  integrationWebhookFormToUpdatePayload,
  integrationWebhookToFormValues,
  webhookStatusLabel,
  type IntegrationWebhook,
  type IntegrationWebhookCreatePayload,
  type IntegrationWebhookFormSchema,
  type IntegrationWebhookStatusRef,
  type IntegrationWebhookUpdatePayload,
} from '../../../models/IntegrationWebhook';

interface IntegrationWebhookDialogProps {
  commerceId: string;
  webhook: IntegrationWebhook | null;
  statuses: IntegrationWebhookStatusRef[];
  isSaving: boolean;
  onClose: () => void;
  onCreate: (payload: IntegrationWebhookCreatePayload) => Promise<boolean>;
  onUpdate: (webhookId: string, payload: IntegrationWebhookUpdatePayload) => Promise<boolean>;
}

const IntegrationWebhookDialog = ({
  commerceId,
  webhook,
  statuses,
  isSaving,
  onClose,
  onCreate,
  onUpdate,
}: IntegrationWebhookDialogProps) => {
  const { control, handleSubmit } = useForm<IntegrationWebhookFormSchema>({
    resolver: zodResolver(integrationWebhookFormSchema),
    defaultValues: integrationWebhookToFormValues(webhook),
    mode: 'onChange',
  });
  const availableStatuses = useMemo<ShippingStatus[]>(
    () =>
      statuses.map((status) => ({
        id: status.id,
        name: status.name,
        commercialName: status.commercialName ?? undefined,
      })),
    [statuses]
  );
  const urlLocked = !!webhook?.lockedFields.includes('url');

  const submit = handleSubmit(async (values) => {
    const saved = webhook
      ? await onUpdate(webhook.id, integrationWebhookFormToUpdatePayload(webhook, values))
      : await onCreate(integrationWebhookFormToCreatePayload(commerceId, values));
    if (saved) onClose();
  });

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ px: 4, pt: 3, pb: 2, fontWeight: 700 }}>
        {webhook ? `Editar webhook · ${webhookStatusLabel(webhook.status)}` : 'Nuevo webhook'}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 4, py: 3 }}>
        <div className='flex flex-col gap-4'>
          <FormTextInput
            control={control}
            name='url'
            placeholder='URL del webhook (https://...)'
            required
            disabled={isSaving || urlLocked}
          />
          {urlLocked && (
            <p className='text-xs text-gray-500'>Manda a varias URLs: se cambian por base.</p>
          )}
          <WebhookHeadersField control={control} name='headers' disabled={isSaving} />
          {!webhook && (
            <Controller
              name='statusIds'
              control={control}
              render={({ field, fieldState }) => (
                <div className='flex flex-col gap-1'>
                  <span className='text-sm text-gray-700'>Estados a notificar</span>
                  <StatusListManager
                    selectedStatusIds={field.value}
                    availableStatuses={availableStatuses}
                    onAdd={(statusId) => field.onChange([...field.value, statusId])}
                    onRemove={(statusId) =>
                      field.onChange(field.value.filter((id) => id !== statusId))
                    }
                    label='Buscar estado a notificar'
                    disabled={isSaving}
                  />
                  {fieldState.error?.message && (
                    <p className='text-xs text-red-600'>{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />
          )}
          {(webhook?.hasBodyTemplate || webhook?.requiresTokenRefresh) && (
            <p className='text-xs text-gray-500'>
              {webhook.hasBodyTemplate && 'Usa una plantilla de cuerpo propia. '}
              {webhook.requiresTokenRefresh && 'Renueva el token antes de cada aviso. '}
              Eso no se cambia desde acá.
            </p>
          )}
          <FormCheckbox control={control} name='active' placeholder='Activo' disabled={isSaving} />
        </div>
      </DialogContent>
      <DialogActions className='px-8 pb-6 pt-5 gap-3'>
        <GlobalButton variant={GlobalButtonVariant.textButton} action={onClose} disabled={isSaving}>
          Cancelar
        </GlobalButton>
        <GlobalButton variant={GlobalButtonVariant.primaryButton} action={submit} disabled={isSaving}>
          Guardar
        </GlobalButton>
      </DialogActions>
    </Dialog>
  );
};

export default IntegrationWebhookDialog;
